import { Injectable } from '@angular/core';
import { SiriusDataService } from '@ibtech/sirius-angular-service';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { SiteLanguageModel, UIModel } from '../models/shared.model';

export const SITE_LANGUAGE_TUR = 'TUR';
export const SITE_LANGUAGE_ENG = 'ENG';

/** Eski sürümde localStorage ile kalıcı tutuluyordu; artık kullanılmıyor. */
const LEGACY_STORAGE_KEY = 'yediemin-site-language-code';

@Injectable({
  providedIn: 'root'
})
export class SiteLanguageService {
  private languageCodeSubject = new BehaviorSubject<string>(SITE_LANGUAGE_TUR);

  readonly languageCode$ = this.languageCodeSubject.asObservable();

  /** İlk LoadPage cevabı gelene kadar tamamlandı mı */
  private firstServerSyncDone = false;
  /**
   * İlk senkron tamamlanmadan kullanıcı dili değiştirdiyse, gecikmiş LoadPage yanıtı
   * (sunucuda henüz TUR) seçilen ENG’i ezmesin.
   */
  private userChangedLanguageBeforeFirstSync = false;

  constructor(private dataService: SiriusDataService) {
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  get languageCode(): string {
    return this.languageCodeSubject.getValue();
  }

  /** Türkçe arayüzde "English", İngilizce arayüzde "Türkçe" — tıklanınca diğer dil seçilir. */
  get toggleLabel(): string {
    return this.languageCode === SITE_LANGUAGE_TUR ? 'English' : 'Türkçe';
  }

  /** Oturumdaki dil kodunu sunucudan alır; kalıcı tarayıcı saklama kullanılmaz. */
  syncFromServer(): void {
    const model = new UIModel();
    this.dataService.post<UIModel>('LoadPage', model).toPromise().then(
      (ui) => this.applyServerResponse(ui),
      () => { }
    );
  }

  private applyServerResponse(ui: UIModel): void {
    if (!ui) {
      return;
    }
    let serverCode = (ui.SiteLanguageCode || SITE_LANGUAGE_TUR).toUpperCase();
    if (serverCode !== SITE_LANGUAGE_TUR && serverCode !== SITE_LANGUAGE_ENG) {
      serverCode = SITE_LANGUAGE_TUR;
    }

    if (!this.firstServerSyncDone) {
      this.firstServerSyncDone = true;
      if (!this.userChangedLanguageBeforeFirstSync) {
        this.languageCodeSubject.next(serverCode);
      }
      return;
    }

    const current = this.languageCodeSubject.getValue();
    if (serverCode !== current) {
      this.languageCodeSubject.next(serverCode);
    }
  }

  private pushLanguageToServer(code: string): Promise<void> {
    const m = new SiteLanguageModel();
    m.LanguageCode = code;
    return this.dataService.post<SiteLanguageModel>('SetSiteLanguage', m).toPromise().then(() => { }, () => { });
  }

  /**
   * Mevcut client dilini sunucu oturumuna yazar ve sunucudan yanıt dönünce
   * resolve olan bir Promise döner. Login akışında (GetDocumentList,
   * StartCaptchaValidation gibi ardışık çağrıların doğru dili görebilmesi
   * için) await edilerek kullanılmalıdır.
   */
  syncLanguageToServer(): Promise<void> {
    return this.pushLanguageToServer(this.languageCode);
  }

  toggleLanguage(): void {
    const next = this.languageCode === SITE_LANGUAGE_TUR ? SITE_LANGUAGE_ENG : SITE_LANGUAGE_TUR;
    this.setLanguage(next);
  }

  setLanguage(code: string): void {
    if (code !== SITE_LANGUAGE_TUR && code !== SITE_LANGUAGE_ENG) {
      return;
    }
    if (!this.firstServerSyncDone) {
      this.userChangedLanguageBeforeFirstSync = true;
    }
    this.languageCodeSubject.next(code);
    this.pushLanguageToServer(code);
  }
}
